/**
 * NE4 (.338): die Karte des Bereichs.
 *
 * ORIGINALTEXT MARVIN (09.10.2026)
 *   „Wie, wo sehe ich die Karte? Wie sehe ich das mit den Terminvorschlägen?
 *    Ich muss das ja mal live testen, bis Unterlagen kommen."
 *
 * EIN BILD UND EIN SVG DARÜBER. Das Hintergrundbild ist die Straßenkarte
 * des Hauses (ein Bild je Ausschnitt, wie beim Lageplan); die Punkte liegen
 * als SVG darüber, gerechnet mit derselben Web-Mercator-Rechnung wie die
 * Tiefbaukarte (`kartenebene.ts`). Kommt kein Bild (kein Dienst), stehen
 * die Punkte auf leerem Grund und die Karte bleibt benutzbar.
 *
 * JEDES GEBIET EINE FARBE, jeder Auftrag ein Kreis: hohl heißt offen, voll
 * heißt terminiert oder ausgeführt, ein Kreuz heißt zurückgegeben. Ein Klick
 * öffnet die Auftragskarte mit den Terminvorschlägen, dieselbe wie in der
 * Auftragsliste. Die Teams stehen als Raute an ihrem Startort.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';
import { api, darf, type Anmelder } from '../api.ts';
import { FirmaHaus } from './Ne4Firma.tsx';
import { Kopf, Karte } from '../teile/Geruest.tsx';
import { Fehlerkasten, ausFehler, type Fehlerlage } from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { ausPixel, nachPixel, kartenHoehe } from '../teile/kartenebene.ts';
import { PunktMenue, RoutenEbene, RoutenKnoepfe, RoutenLeiste, haltAus } from '../teile/Fahrtroute.tsx';
import { AuftragsKarte, ZUSTAND, tagLang } from './Ne4Arbeit.tsx';

const FARBEN = ['#0f6e56', '#1f3864', '#b45309', '#7c3aed', '#be123c', '#0e7490', '#4d7c0f', '#9f1239', '#1d4ed8', '#a16207'];
const OHNE = '#6b7280';
const HOEHE = 560;
/* NÄHER RAN (.521). Marvin, 28.10.2026: „Ende mit dem Zoomen ... Ich kann die
   Häuser gar nicht auseinanderziehen. Das muss noch kleiner gehen.“ Kacheln
   bis Stufe 19 (die Straßenkarte gibt sie her), dazwischen und bis 20
   vergrößert. */
const MAXZ = 20; const MAXZ_KACHEL = 19;

type P = { lat: number; lon: number };

function passendeStufe(punkte: P[], breite: number, hoehe: number): { mitte: P; zoom: number } {
  if (!punkte.length) return { mitte: { lat: 50.36, lon: 7.59 }, zoom: 11 };
  const lats = punkte.map((p) => p.lat), lons = punkte.map((p) => p.lon);
  const mitte = { lat: (Math.min(...lats) + Math.max(...lats)) / 2, lon: (Math.min(...lons) + Math.max(...lons)) / 2 };
  for (let z = 15; z >= 8; z--) {
    const a = nachPixel({ lat: Math.max(...lats), lng: Math.min(...lons) }, z);
    const b = nachPixel({ lat: Math.min(...lats), lng: Math.max(...lons) }, z);
    if (Math.abs(b.x - a.x) < breite - 24 && Math.abs(b.y - a.y) < hoehe - 24) return { mitte, zoom: z };
  }
  return { mitte, zoom: 8 };
}

export function Ne4Karte({ ich }: { ich: Anmelder }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  /* DER AUSSCHNITT ALS PIXEL (.364): Mitte in Weltpixeln der Stufe `zoom`.
     So schiebt ein Mausrad oder ein Finger ohne Umrechnung über Breitengrade,
     und Bild (Kacheln) und Punkte rechnen aus derselben Zahl. */
  const [sicht, setSicht] = useState<{ cx: number; cy: number; zoom: number } | null>(null);
  const [breite, setBreite] = useState(900);
  const [offen, setOffen] = useState<string | null>(null);
  /* ORTSNETZE ALS CHIPS (.563). Marvin, 30.10.2026: „ich will hier die
     Ortsnetze anklicken, selektieren und deselektieren können als Chip ...
     alle an, alle aus ... dass ich mehrere Ortsnetze mir anzeigen lassen
     kann." Gemerkt wird, was AUS ist: ein neues Gebiet erscheint von selbst. */
  const [aus, setAus] = useState<Set<string>>(() => new Set());
  const umschalten = (id: string) => { setAus((a) => { const n = new Set(a); if (n.has(id)) n.delete(id); else n.add(id); return n; }); setSicht(null); };
  /* .521: liegen Häuser auch auf der letzten Stufe übereinander, wählt man aus einer kleinen Liste. */
  const [auswahl, setAuswahl] = useState<any[] | null>(null);
  /* .586: ein Klick auf einen Punkt öffnet erst das kleine Fenster (Auftrag öffnen, Route: Start, Route: Stopp). */
  const [menue, setMenue] = useState<{ x: number; y: number; p: any } | null>(null);
  /* .583: DIESELBE AUSWAHL WIE IN DEN AUFTRÄGEN. Marvin, 02.11.2026: „In der
     Karte muss auch auswählbar sein, wie bei den Aufträgen, ne? NE4 wählbar,
     wartet, neue Auskundung". */
  const [zustandWahl, setZustandWahl] = useState('');
  const [gbgsWahl, setGbgsWahl] = useState('');
  const rahmen = useRef<HTMLDivElement>(null);
  const zieh = useRef<{ x: number; y: number; cx: number; cy: number; bewegt: boolean } | null>(null);
  const finger = useRef<{ x: number; y: number; abstand: number; cx: number; cy: number; zoom: number } | null>(null);
  const abstand = (t: React.TouchList) => t.length < 2 ? 0 : Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);
  const mitte: P | null = sicht ? (() => { const g = ausPixel(sicht.cx, sicht.cy, sicht.zoom); return { lat: g.lat, lon: g.lng }; })() : null;
  const zoom = sicht?.zoom ?? 11;
  const setzeMitte = (p: P | null, z?: number) => {
    if (!p) { setSicht(null); return; }
    const zz = z ?? zoom;
    const q = nachPixel({ lat: p.lat, lng: p.lon }, zz);
    setSicht({ cx: q.x, cy: q.y, zoom: zz });
  };
  /* DIE KARTE IN VOLLER GRÖSSE (.513). Marvin, 28.10.2026: „die Karte hier
     sozusagen voll mit sein, volle Größe. Wir verlieren hier ja den Platz
     zur Karte… Legende oben rechts hin und dann die Seite beginnen." Bis
     .512 stand die Gebieteliste als Spalte links und die Karte war 560 Pixel
     hoch, darunter blieb die Seite leer. Jetzt liegt die Legende als Zeile
     oben rechts, die Karte nimmt die ganze Breite und reicht bis zum unteren
     Rand des Fensters (gemessen, nicht geraten). */
  const [hoehe, setHoehe] = useState(HOEHE);

  const laden = () => api.ne4Karte().then(setD).catch((f) => setFehler(ausFehler(f)));
  useEffect(() => { laden(); }, []);
  /* AUS DER GLOCKE HERGEKOMMEN (.364): der Auftrag wurde unter
     mds.ne4.oeffne abgelegt und geht hier gleich auf. */
  useEffect(() => {
    try {
      const id = sessionStorage.getItem('mds.ne4.oeffne');
      if (id) { sessionStorage.removeItem('mds.ne4.oeffne'); setOffen(id); }
    } catch { /* ohne Speicher nichts zu öffnen */ }
  }, []);
  useFrisch(laden);
  useEffect(() => {
    const messen = () => {
      const el = rahmen.current; if (!el) return;
      setBreite(Math.max(300, Math.floor(el.clientWidth) - 2));
      /* Vom oberen Rand der Karte bis zum Fensterende, 36 Pixel bleiben für
         den Hinweis darunter und den Seitenrand. */
      setHoehe(kartenHoehe(el));
    };
    messen(); window.addEventListener('resize', messen);
    /* AUCH WENN NUR DER PLATZ WECHSELT (.515), etwa weil eine Rollleiste kommt
       oder das Menü zuklappt: sonst ragt die Karte rechts hinaus (Marvin,
       28.10.2026: „Die Karte ist abgeschnitten, ist zu groß"). */
    const ro = typeof ResizeObserver !== 'undefined' && rahmen.current ? new ResizeObserver(() => messen()) : null;
    if (ro && rahmen.current) ro.observe(rahmen.current);
    return () => { window.removeEventListener('resize', messen); ro?.disconnect(); };
  }, [!!d]);
  /* DAS RAD ÜBER DER KARTE ROLLT NICHT DIE SEITE (.400). Reacts onWheel ist
     passiv und darf das nicht verhindern; ein eigener Zuhörer darf. */
  useEffect(() => {
    const el = rahmen.current; if (!el) return;
    const halt = (e: WheelEvent) => { if ((e.target as HTMLElement)?.closest?.('.ne4-karte-rahmen')) e.preventDefault(); };
    el.addEventListener('wheel', halt, { passive: false });
    return () => el.removeEventListener('wheel', halt);
  }, [!!d]);
  /* Ein Punkt ohne Gebiet zählt zu keinem Chip und steht nur da, solange alle an sind. */
  function passt(p: any): boolean {
    if (zustandWahl === 'offen' ? !['import', 'angeschrieben', 'wartet'].includes(p.zustand) : zustandWahl && p.zustand !== zustandWahl) return false;
    if (gbgsWahl === 'waehlbar' && p.gbgs_zustand !== 'waehlbar') return false;
    if (gbgsWahl === 'wartet' && p.gbgs_zustand !== 'wartet') return false;
    if (gbgsWahl === 'auskundung_neu' && !p.gbgs_auskundung_neu) return false;
    return true;
  }
  function zeigen(p: any): boolean { return passt(p) && (p.gebiet_id ? !aus.has(p.gebiet_id) : aus.size === 0); }
  /* „ALLES ZEIGEN" SETZT DIE MITTE AUF NULL, und dieser Effekt rechnet sie
     neu. Bis .344 hing er nur an `d` und `breite`: nach dem Klick blieb die
     Mitte leer, das Bild weg, die Punkte standen oben links (Marvin,
     11.10.2026: „bekomme ich einen Seitenladefehler"). */
  useEffect(() => {
    if (!d || sicht) return;
    const sichtbar = (d.punkte ?? []).filter((x: any) => zeigen(x));
    const p = passendeStufe((sichtbar.length ? sichtbar : d.punkte ?? []).map((x: any) => ({ lat: Number(x.lat), lon: Number(x.lon) })), breite, hoehe);
    setzeMitte(p.mitte, p.zoom);
  }, [d, breite, sicht, aus, zustandWahl, gbgsWahl]); // eslint-disable-line react-hooks/exhaustive-deps

  const farbe = useMemo(() => {
    const m = new Map<string, string>();
    (d?.gebiete ?? []).forEach((g: any, i: number) => m.set(g.id, FARBEN[i % FARBEN.length]!));
    return (id: string | null) => (id && m.get(id)) || OHNE;
  }, [d]);

  useEffect(() => { setMenue(null); }, [sicht]); // .586: nach Schieben oder Zoomen steht das Fenster nicht mehr am Punkt
  const lage = (p: P) => {
    if (!sicht) return { x: 0, y: 0 };
    const q = nachPixel({ lat: p.lat, lng: p.lon }, sicht.zoom);
    return { x: q.x - sicht.cx + breite / 2, y: q.y - sicht.cy + hoehe / 2 };
  };
  /* Ein Schirmpunkt der Karte als Breite und Länge (für den Sprung auf eine Gruppe). */
  const ausPixelLage = (px: number, py: number): P => { if (!sicht) return { lat: 50.1, lon: 8.6 }; const g = ausPixel(sicht.cx + px - breite / 2, sicht.cy + py - hoehe / 2, sicht.zoom); return { lat: g.lat, lon: g.lng }; };
  const schieben = (dx: number, dy: number) => setSicht((s) => s ? { ...s, cx: s.cx + dx, cy: s.cy + dy } : s);
  /* ZOOMEN UM EINEN PUNKT: der Ort unter dem Zeiger bleibt, wo er ist.
     STUFENLOS (.400). Marvin, 19.10.2026: „Bei der Kartenansicht läuft das
     rein und rauszoomen nicht flüssig." Bis .399 sprang jede Radstufe und
     jeder Fingerzug um eine ganze Kachelstufe, und bei jedem Sprung kamen
     alle Kacheln neu. Jetzt ist `zoom` eine Kommazahl: Punkte rechnen mit
     ihr, die Kacheln kommen aus der nächsten ganzen Stufe und werden
     dazwischen vergrößert (siehe `kacheln`). Neue Kacheln gibt es nur, wenn
     die ganze Stufe wechselt. */
  const zoomenUm = (n: number, px?: number, py?: number) => setSicht((s) => {
    if (!s) return s;
    const z2 = Math.max(8, Math.min(MAXZ, s.zoom + n));
    if (z2 === s.zoom) return s;
    const f = 2 ** (z2 - s.zoom);
    const ox = (px ?? breite / 2) - breite / 2, oy = (py ?? hoehe / 2) - hoehe / 2;
    return { zoom: z2, cx: (s.cx + ox) * f - ox, cy: (s.cy + oy) * f - oy };
  });
  /* Die Knöpfe + und − gehen auf die nächste ganze Stufe. */
  const zoomenStufe = (n: number) => setSicht((s) => {
    if (!s) return s;
    const ziel = n > 0 ? Math.floor(s.zoom + 1e-6) + 1 : Math.ceil(s.zoom - 1e-6) - 1;
    const z2 = Math.max(8, Math.min(MAXZ, ziel));
    if (z2 === s.zoom) return s;
    const f = 2 ** (z2 - s.zoom);
    return { zoom: z2, cx: s.cx * f, cy: s.cy * f };
  });
  const zoomen = (n: number) => zoomenStufe(n);
  const maus = {
    onMouseDown: (e: React.MouseEvent) => { if (!sicht || e.button !== 0) return; zieh.current = { x: e.clientX, y: e.clientY, cx: sicht.cx, cy: sicht.cy, bewegt: false }; },
    onMouseMove: (e: React.MouseEvent) => {
      const z = zieh.current; if (!z || !(e.buttons & 1)) return;
      const dx = z.x - e.clientX, dy = z.y - e.clientY;
      if (!z.bewegt && Math.abs(dx) + Math.abs(dy) < 4) return;
      z.bewegt = true;
      setSicht((s) => s ? { ...s, cx: z.cx + dx, cy: z.cy + dy } : s);
    },
    onMouseUp: () => { zieh.current = null; },
    onMouseLeave: () => { zieh.current = null; },
    onWheel: (e: React.WheelEvent) => {
      const r = rahmen.current?.getBoundingClientRect(); if (!r) return;
      /* Ein Rad-Tick (deltaMode 1 = Zeilen) ist eine Viertelstufe, ein
         Trackpad (Pixel) läuft feiner. Begrenzt, damit ein Ruck nicht
         über mehrere Stufen springt. */
      const roh = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const schritt = Math.max(-0.5, Math.min(0.5, -roh / 200));
      if (schritt) zoomenUm(schritt, e.clientX - r.left, e.clientY - r.top);
    },
  };
  /* FINGER AUF DEM TELEFON (.345, .364): ein Finger schiebt live, zwei
     Finger zoomen um ihre Mitte. */
  const beruehrung = {
    onTouchStart: (e: React.TouchEvent) => {
      if (!sicht) return;
      const t = e.touches;
      const x = t.length >= 2 ? (t[0]!.clientX + t[1]!.clientX) / 2 : (t[0]?.clientX ?? 0);
      const y = t.length >= 2 ? (t[0]!.clientY + t[1]!.clientY) / 2 : (t[0]?.clientY ?? 0);
      finger.current = { x, y, abstand: abstand(t), cx: sicht.cx, cy: sicht.cy, zoom: sicht.zoom };
    },
    onTouchMove: (e: React.TouchEvent) => {
      const f = finger.current; if (!f) return;
      if (e.cancelable) e.preventDefault();
      const t = e.touches;
      if (t.length >= 2 && f.abstand > 0) {
        const q = abstand(t) / f.abstand;
        const r = rahmen.current?.getBoundingClientRect();
        const mx = (t[0]!.clientX + t[1]!.clientX) / 2 - (r?.left ?? 0), my = (t[0]!.clientY + t[1]!.clientY) / 2 - (r?.top ?? 0);
        const z2 = Math.max(8, Math.min(MAXZ, f.zoom + Math.log2(q)));
        const fak = 2 ** (z2 - f.zoom);
        const ox = mx - breite / 2, oy = my - hoehe / 2;
        setSicht({ zoom: z2, cx: (f.cx + ox) * fak - ox, cy: (f.cy + oy) * fak - oy });
        return;
      }
      const dx = f.x - (t[0]?.clientX ?? f.x), dy = f.y - (t[0]?.clientY ?? f.y);
      setSicht((s) => s ? { ...s, zoom: f.zoom, cx: f.cx + dx, cy: f.cy + dy } : s);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      if (e.touches.length === 0) finger.current = null;
      else if (sicht) { const t = e.touches; finger.current = { x: t[0]!.clientX, y: t[0]!.clientY, abstand: 0, cx: sicht.cx, cy: sicht.cy, zoom: sicht.zoom }; }
    },
  };
  /* DIE KACHELN DES AUSSCHNITTS, aus der nächsten ganzen Stufe, auf die
     Kommastufe skaliert (.400): bei zoom 12,4 kommen die Kacheln der Stufe
     12 und werden um 2^0,4 vergrößert. Sie bleiben stehen, bis die ganze
     Stufe wechselt; dazwischen ruckelt nichts. */
  const kacheln = useMemo(() => {
    if (!sicht) return [] as { key: string; src: string; x: number; y: number; g: number }[];
    const zt = Math.max(8, Math.min(MAXZ_KACHEL, Math.round(sicht.zoom)));
    const sk = 2 ** (sicht.zoom - zt);          // Vergrößerung der Kacheln
    const g = 256 * sk;                          // Kachelgröße auf dem Schirm
    const n = 2 ** zt;
    const cxT = sicht.cx / sk, cyT = sicht.cy / sk;   // die Mitte in Pixeln der Kachelstufe
    const x0 = Math.floor((cxT - breite / 2 / sk) / 256), x1 = Math.floor((cxT + breite / 2 / sk) / 256);
    const y0 = Math.floor((cyT - hoehe / 2 / sk) / 256), y1 = Math.floor((cyT + hoehe / 2 / sk) / 256);
    const aus: { key: string; src: string; x: number; y: number; g: number }[] = [];
    for (let tx = x0; tx <= x1; tx++) for (let ty = y0; ty <= y1; ty++) {
      if (ty < 0 || ty >= n) continue;
      const wx = ((tx % n) + n) % n;
      aus.push({ key: `${zt}/${tx}/${ty}`, src: `/api/ne4/kachel/${zt}/${wx}/${ty}`,
                 x: (tx * 256 - cxT) * sk + breite / 2, y: (ty * 256 - cyT) * sk + hoehe / 2, g });
    }
    return aus;
  }, [sicht, breite, hoehe]);

  const punkte = (d?.punkte ?? []).filter((p: any) => zeigen(p));
  const teams = d?.teams ?? [];

  /* .592: EINE FIRMA ÖFFNET DAS HAUS WIE IN „MEINE AUFTRÄGE" (ohne Dispo-Akte, ohne Preise). */
  if (offen && !darf(ich, 'ne4.auftraege.lesen') && darf(ich, 'ne4.firmenauftraege.lesen')) return <FirmaHaus ich={ich} id={offen} zu={() => setOffen(null)} />;
  return (
    <>
      <Kopf titel="NE4, Karte" weg={d ? `${d.punkte.length} Aufträge mit Punkt, ${d.gebiete.length} Gebiete, ${teams.length} Teams` : ''}
            rechts={<div className="reihe">
              <button type="button" className="knopf klein" onClick={() => zoomen(1)} aria-label="Näher">+</button>
              <button type="button" className="knopf klein" onClick={() => zoomen(-1)} aria-label="Weiter weg">−</button>
            </div>} />
      <div className="inhalt">
      <Fehlerkasten lage={fehler} />
      {d && !d.punkte.length && <Karte><div className="leise">Noch kein Auftrag mit Koordinaten. Der Stundenlauf „Koordinaten" sucht die Punkte.</div></Karte>}
      <div className="ne4-karte" style={{ padding: '0 10px 0 2px' }}>
        <div className="ne4-karte-legende">
          <span className="klein leise ne4-karte-zeichen">
            {/* .595: eine Firma sieht nur ihre terminierten Häuser; „offen" und das Bulk-Portal gehen sie nichts an. */}
            {d?.firma ? <><span className="ne4-farbpunkt" style={{ background: '#444' }} /> terminiert oder ausgeführt (Ihre Häuser)</>
              : <><span className="ne4-farbpunkt hohl" /> offen, <span className="ne4-farbpunkt" style={{ background: '#444' }} /> terminiert oder ausgeführt, ✕ zurückgegeben, ◆ Team am Startort</>}
          </span>
          <span className="wachsen" />
          {!!d && !d.gebiete.length && <span className="klein leise">Noch kein Gebiet.</span>}
          {(d?.gebiete ?? []).map((g: any) => (
            <button key={g.id} type="button" className={'ne4-gebiet-zeile ne4-gebiet-chip' + (aus.has(g.id) ? ' aus' : ' gewaehlt')}
                    title={(g.plz ?? []).join(', ')} aria-pressed={!aus.has(g.id)}
                    onClick={() => umschalten(g.id)}>
              <span className="ne4-farbpunkt" style={{ background: farbe(g.id) }} />
              <span>{g.name}</span>
              <span className="klein leise">{d?.firma ? `${g.terminiert ?? 0} terminiert` : `${g.offen} offen`}</span>
            </button>
          ))}
          <select className="ne4-karte-wahl" value={zustandWahl} title="Zustand wie in den Aufträgen" onChange={(e) => { setZustandWahl(e.target.value); setSicht(null); }}>
            <option value="">Zustand: alle</option>
            {!d?.firma && <option value="offen">offen (neu, angeschrieben, wartet)</option>}
            {Object.entries(ZUSTAND).filter(([k]) => !d?.firma || ['terminiert', 'ausgefuehrt'].includes(k)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {!d?.firma && <select className="ne4-karte-wahl" value={gbgsWahl} title="Stand im Bulk-Portal, wie in den Aufträgen" onChange={(e) => { setGbgsWahl(e.target.value); setSicht(null); }}>
            <option value="">GBGS Bulk: alle</option>
            <option value="waehlbar">NE4 wählbar ({(d?.punkte ?? []).filter((p: any) => p.gbgs_zustand === 'waehlbar').length})</option>
            <option value="wartet">NE4 wartet ({(d?.punkte ?? []).filter((p: any) => p.gbgs_zustand === 'wartet').length})</option>
            <option value="auskundung_neu">neue Auskundung ({(d?.punkte ?? []).filter((p: any) => p.gbgs_auskundung_neu).length})</option>
          </select>}
          {!!d?.gebiete?.length && <>
            <button type="button" className="knopf klein" disabled={!aus.size} onClick={() => { setAus(new Set()); setSicht(null); }}>Alle an</button>
            <button type="button" className="knopf klein" disabled={aus.size === d.gebiete.length} onClick={() => { setAus(new Set(d.gebiete.map((g: any) => g.id))); setSicht(null); }}>Alle aus</button>
          </>}
        </div>
        <RoutenLeiste oeffnen={(h) => setOffen(h.id)} />
        <div className="ne4-karte-flaeche" ref={rahmen}>
          <div className="ne4-karte-rahmen" style={{ height: hoehe }} {...maus} {...beruehrung}>
            {kacheln.map((k) => (
              <img key={k.key} src={k.src} alt="" width={256} height={256} draggable={false}
                   style={{ left: k.x, top: k.y, width: k.g + 0.5, height: k.g + 0.5 }} onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
            ))}
            <svg width={breite} height={hoehe} viewBox={`0 0 ${breite} ${hoehe}`}>
              {teams.map((t: any, i: number) => {
                const g = (d?.gebiete ?? []).find((x: any) => (x.plz ?? []).includes(t.start_plz));
                const p = punkte.find((x: any) => x.plz === t.start_plz);
                const ort: P | null = p ? { lat: Number(p.lat), lon: Number(p.lon) } : g?.lat ? { lat: Number(g.lat), lon: Number(g.lon) } : null;
                if (!ort) return null;
                const { x, y } = lage(ort);
                return (
                  <g key={t.id} style={{ pointerEvents: 'none' }}>
                    <title>{`${t.name} (${t.firma}), Start ${t.start_plz ?? ''} ${t.start_ort ?? ''}`}</title>
                    <path d={`M${x},${y - 11 - i * 2}L${x + 8},${y - 3 - i * 2}L${x},${y + 5 - i * 2}L${x - 8},${y - 3 - i * 2}Z`} fill="#111" stroke="#fff" strokeWidth={1.5} />
                    <text x={x + 11} y={y - i * 2} fontSize={11} fill="#111" stroke="#fff" strokeWidth={3} paintOrder="stroke">{t.name}</text>
                  </g>
                );
              })}
              {/* ÜBERLAPPENDE PUNKTE TRAGEN EINE ZAHL (.520). Marvin, 28.10.2026:
                  „Wenn wir hier mehrere Häuser in einer Straße haben ... und das
                  ist überlappt, dann muss da eine Zahl stehen ... Wie bei GBGS."
                  Punkte, die auf dem Schirm näher als 16 Pixel beieinander
                  liegen, werden ein Kreis mit der Zahl; ein Klick zoomt dorthin,
                  auf der letzten Stufe öffnet er das erste Haus. */}
              {(() => {
                const gruppen: { x: number; y: number; p: any[] }[] = [];
                for (const p of punkte) {
                  const { x, y } = lage({ lat: Number(p.lat), lon: Number(p.lon) });
                  if (x < -20 || y < -20 || x > breite + 20 || y > hoehe + 20) continue;
                  const g = gruppen.find((q) => Math.hypot(q.x - x, q.y - y) < 16);
                  if (g) { g.p.push(p); g.x = (g.x * (g.p.length - 1) + x) / g.p.length; g.y = (g.y * (g.p.length - 1) + y) / g.p.length; }
                  else gruppen.push({ x, y, p: [p] });
                }
                return gruppen.map((g) => {
                  if (g.p.length > 1) {
                    const f = farbe(g.p[0].gebiet_id);
                    const titel = g.p.map((p: any) => `${p.strasse ?? ''} ${p.hausnummer ?? ''} (${p.kls_id})`).join('\n');
                    return (
                      <g key={'g' + g.p[0].id} className="ne4-punkt" style={{ cursor: 'pointer' }}
                         onClick={() => { if (zieh.current?.bewegt) return; if (zoom < MAXZ_KACHEL) setzeMitte(ausPixelLage(g.x, g.y), Math.min(MAXZ_KACHEL, zoom + 4)); else setAuswahl(g.p); }}>
                        <title>{`${g.p.length} Häuser:\n${titel}`}</title>
                        <circle cx={g.x} cy={g.y} r={12} fill={f} stroke="#fff" strokeWidth={2.5} />
                        <text x={g.x} y={g.y + 4} fontSize={11} fontWeight={700} fill="#fff" textAnchor="middle">{g.p.length}</text>
                      </g>
                    );
                  }
                  const p = g.p[0]; const x = g.x, y = g.y;
                  const f = farbe(p.gebiet_id);
                  const voll = p.zustand === 'terminiert' || p.zustand === 'ausgefuehrt';
                  const titel = `${p.kls_id}, ${p.name ?? ''}, ${p.strasse ?? ''} ${p.hausnummer ?? ''}, ${p.plz ?? ''} ${p.ort ?? ''}, ${ZUSTAND[p.zustand] ?? p.zustand}${p.termin ? `, Termin ${tagLang(String(p.termin).slice(0, 10))}` : ''}`;
                  return (
                    <g key={p.id} className="ne4-punkt" onClick={() => { if (!zieh.current?.bewegt) setMenue({ x, y, p }); }} style={{ cursor: 'pointer' }}>
                      <title>{titel}</title>
                      {p.zustand === 'zurueckgegeben'
                        ? <path d={`M${x - 5},${y - 5}L${x + 5},${y + 5}M${x + 5},${y - 5}L${x - 5},${y + 5}`} stroke={f} strokeWidth={2.5} />
                        : <circle cx={x} cy={y} r={6} fill={voll ? f : '#fff'} stroke={f} strokeWidth={2.5} />}
                    </g>
                  );
                });
              })()}
              <RoutenEbene lage={(o) => lage({ lat: o.lat, lon: o.lon })} />
            </svg>
            {menue && <PunktMenue x={menue.x} y={menue.y} breite={breite} titel={`${menue.p.strasse ?? ''} ${menue.p.hausnummer ?? ''}, ${menue.p.kls_id ?? ''}`} h={haltAus('ne4', menue.p)}
                                  oeffnen={() => { const id = menue.p.id; setMenue(null); setOffen(id); }} zu={() => setMenue(null)} />}
            {auswahl && (
              <div className="ne4-karte-auswahl">
                <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'center' }}><b>{auswahl.length} Häuser an diesem Punkt</b><button type="button" className="knopf klein" onClick={() => setAuswahl(null)}>schließen</button></div>
                {auswahl.map((p: any) => (
                  <div key={p.id} className="routen-gruppenzeile">
                    <button type="button" className="ne4-gebiet-zeile" onClick={() => { setAuswahl(null); setOffen(p.id); }}>
                      <span className="wachsen">{p.strasse} {p.hausnummer}<span className="klein leise">, {p.kls_id}, {ZUSTAND[p.zustand] ?? p.zustand}</span></span>
                    </button>
                    <RoutenKnoepfe h={haltAus('ne4', p)} />
                  </div>
                ))}
              </div>
            )}
            <div className="ne4-karte-pfeile">
              <button type="button" className="knopf klein" onClick={() => schieben(0, -hoehe / 3)} aria-label="Nach Norden">▲</button>
              <div><button type="button" className="knopf klein" onClick={() => schieben(-breite / 3, 0)} aria-label="Nach Westen">◀</button><button type="button" className="knopf klein" onClick={() => schieben(breite / 3, 0)} aria-label="Nach Osten">▶</button></div>
              <button type="button" className="knopf klein" onClick={() => schieben(0, hoehe / 3)} aria-label="Nach Süden">▼</button>
            </div>
          </div>
          <div className="klein leise" style={{ marginTop: 6 }}>Ziehen schiebt den Ausschnitt, das Mausrad zoomt um den Zeiger (auf dem Telefon mit 2 Fingern), ein Klick auf einen Punkt: Auftrag öffnen oder in die Route (Start, Stopp).</div>
        </div>
      </div>
      {offen && <AuftragsKarte ich={ich} id={offen} zu={() => setOffen(null)} geaendert={laden} />}
      </div>
    </>
  );
}
